import React from 'react';
import * as Icons from 'lucide-react';
import { WIDGETS, getWidgetsForDomain } from '../../config/widgets';
import { DOMAINS, getDefaultLayout } from '../../config/domains';
import { ABLE_COLORS } from '../../constants';

/**
 * WidgetSettings - Modal to customize widgets for a domain
 */
export default function WidgetSettings({
  domain,
  currentLayout,
  onUpdateLayout,
  onResetLayout,
  onClose,
}) {
  const domainConfig = DOMAINS[domain];
  const availableWidgets = getWidgetsForDomain(domain);
  const defaultLayout = getDefaultLayout(domain);
  
  // Split into enabled and disabled widgets
  const enabledWidgets = currentLayout
    .map(id => WIDGETS[id])
    .filter(Boolean);
  
  const disabledWidgets = availableWidgets
    .filter(widget => !currentLayout.includes(widget.id));

  // Get icon component
  const getIcon = (iconName) => {
    return Icons[iconName] || Icons.Circle;
  };

  // Toggle a widget on/off
  const toggleWidget = (widgetId) => {
    if (currentLayout.includes(widgetId)) {
      // Remove
      onUpdateLayout(currentLayout.filter(id => id !== widgetId));
    } else {
      // Add to end
      onUpdateLayout([...currentLayout, widgetId]);
    }
  };

  // Move widget up or down
  const moveWidget = (widgetId, direction) => {
    const index = currentLayout.indexOf(widgetId);
    if (index === -1) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= currentLayout.length) return;

    const newLayout = [...currentLayout];
    [newLayout[index], newLayout[newIndex]] = [newLayout[newIndex], newLayout[index]];
    onUpdateLayout(newLayout);
  };

  // Check if current layout differs from default
  const isCustomized = JSON.stringify(currentLayout) !== JSON.stringify(defaultLayout);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.4)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
    }}
    onClick={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}
    >
      <div style={{
        background: 'white',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '400px',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          borderBottom: `1px solid ${ABLE_COLORS.border}`,
        }}>
          <div>
            <h2 style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: 600,
              color: ABLE_COLORS.textPrimary,
            }}>
              Customize {domainConfig?.name || 'Dashboard'}
            </h2>
            <p style={{
              margin: '4px 0 0',
              fontSize: '13px',
              color: ABLE_COLORS.textMuted,
            }}>
              Toggle widgets on or off, drag to reorder
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              padding: '8px',
              cursor: 'pointer',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icons.X size={20} color={ABLE_COLORS.textMuted} />
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px 20px',
        }}>
          {/* Enabled widgets */}
          {enabledWidgets.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{
                margin: '0 0 12px',
                fontSize: '12px',
                fontWeight: 600,
                color: ABLE_COLORS.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}>
                Showing
              </h3>
              
              {enabledWidgets.map((widget, index) => {
                const Icon = getIcon(widget.icon);
                return (
                  <div
                    key={widget.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px 12px',
                      background: '#F9F9F8',
                      borderRadius: '8px',
                      marginBottom: '8px',
                    }}
                  >
                    {/* Drag handle */}
                    <Icons.GripVertical 
                      size={16} 
                      color={ABLE_COLORS.textMuted}
                      style={{ cursor: 'grab', flexShrink: 0 }}
                    />
                    
                    {/* Icon */}
                    <Icon size={18} color={ABLE_COLORS.primary} style={{ flexShrink: 0 }} />
                    
                    {/* Name */}
                    <span style={{
                      flex: 1,
                      fontSize: '14px',
                      fontWeight: 500,
                      color: ABLE_COLORS.textPrimary,
                    }}>
                      {widget.name}
                    </span>
                    
                    {/* Move buttons */}
                    <button
                      onClick={() => moveWidget(widget.id, 'up')}
                      disabled={index === 0}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '4px',
                        cursor: index === 0 ? 'default' : 'pointer',
                        opacity: index === 0 ? 0.3 : 1,
                        borderRadius: '4px',
                      }}
                    >
                      <Icons.ChevronUp size={16} color={ABLE_COLORS.textMuted} />
                    </button>
                    <button
                      onClick={() => moveWidget(widget.id, 'down')}
                      disabled={index === enabledWidgets.length - 1}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '4px',
                        cursor: index === enabledWidgets.length - 1 ? 'default' : 'pointer',
                        opacity: index === enabledWidgets.length - 1 ? 0.3 : 1,
                        borderRadius: '4px',
                      }}
                    >
                      <Icons.ChevronDown size={16} color={ABLE_COLORS.textMuted} />
                    </button>
                    
                    {/* Hide button */}
                    <button
                      onClick={() => toggleWidget(widget.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '4px 8px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 500,
                        color: ABLE_COLORS.textMuted,
                        borderRadius: '4px',
                      }}
                    >
                      Hide
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Disabled widgets */}
          {disabledWidgets.length > 0 && (
            <div>
              <h3 style={{
                margin: '0 0 12px',
                fontSize: '12px',
                fontWeight: 600,
                color: ABLE_COLORS.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}>
                Available
              </h3>
              
              {disabledWidgets.map(widget => {
                const Icon = getIcon(widget.icon);
                return (
                  <div
                    key={widget.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px 12px',
                      background: 'transparent',
                      borderRadius: '8px',
                      marginBottom: '8px',
                      border: `1px dashed ${ABLE_COLORS.border}`,
                    }}
                  >
                    {/* Icon */}
                    <Icon size={18} color={ABLE_COLORS.textMuted} style={{ flexShrink: 0 }} />
                    
                    {/* Name and description */}
                    <div style={{ flex: 1 }}>
                      <span style={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: ABLE_COLORS.textSecondary,
                      }}>
                        {widget.name}
                      </span>
                      <span style={{
                        display: 'block',
                        fontSize: '12px',
                        color: ABLE_COLORS.textMuted,
                      }}>
                        {widget.description}
                      </span>
                    </div>
                    
                    {/* Show button */}
                    <button
                      onClick={() => toggleWidget(widget.id)}
                      style={{
                        background: ABLE_COLORS.primaryLight,
                        border: 'none',
                        padding: '6px 12px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 500,
                        color: ABLE_COLORS.primary,
                        borderRadius: '6px',
                      }}
                    >
                      Show
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 20px',
          borderTop: `1px solid ${ABLE_COLORS.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <button
            onClick={onResetLayout}
            disabled={!isCustomized}
            style={{
              background: 'none',
              border: 'none',
              padding: '8px 12px',
              cursor: isCustomized ? 'pointer' : 'default',
              fontSize: '13px',
              fontWeight: 500,
              color: isCustomized ? ABLE_COLORS.textSecondary : ABLE_COLORS.textMuted,
              borderRadius: '6px',
              opacity: isCustomized ? 1 : 0.5,
            }}
          >
            Reset to default
          </button>
          
          <button
            onClick={onClose}
            style={{
              background: ABLE_COLORS.primary,
              border: 'none',
              padding: '10px 20px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              color: 'white',
              borderRadius: '8px',
            }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
